/** Pure chart helpers. Colours arrive already resolved from CSS tokens (see hooks/useChartColors.ts). */

export interface ChartSeries {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
  fill: boolean;
  tension: number;
  pointRadius: number;
  borderWidth: number;
}

const clampAlpha = (a: number) => (Number.isFinite(a) ? Math.min(1, Math.max(0, a)) : 1);

/** Applies an alpha to a hex, rgb()/hsl()/oklch() (and friends) colour string. Unknown formats are returned unchanged. */
export function withAlpha(color: string, a: number): string {
  const c = color.trim();
  const alpha = clampAlpha(a);
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(c);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((x) => x + x).join('') : hex[1];
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }
  const fn = /^([a-z-]+)\((.*)\)$/i.exec(c);
  if (fn) {
    const body = fn[2].replace(/\s*\/\s*[^/]*$/, '').trim();
    if (body.includes(',')) {
      // legacy comma syntax: rgb(1, 2, 3) -> rgba(1, 2, 3, a)
      const parts = body.split(',').map((p) => p.trim()).slice(0, 3);
      return `${fn[1].replace(/a$/i, '')}a(${parts.join(', ')}, ${alpha})`;
    }
    return `${fn[1]}(${body} / ${alpha})`;
  }
  return c;
}

/** Builds a Chart.js line dataset with the shared look (soft fill, smooth line, no points). */
export function buildSeries(label: string, color: string, data: number[]): ChartSeries {
  return {
    label,
    data,
    borderColor: color,
    backgroundColor: withAlpha(color, 0.12),
    fill: true,
    tension: 0.3,
    pointRadius: 0,
    borderWidth: 2,
  };
}

const fmt = (n: number) => (Math.round(n * 10) / 10).toFixed(1);

/** Text alternative for a chart series, e.g. "CPU %: latest 12.4, min 3.1, max 40.2 over 60 points". */
export function summarizeSeries(label: string, values: number[], unit = ''): string {
  const v = values.filter((x) => Number.isFinite(x));
  if (v.length === 0) return `${label}: no data`;
  const u = unit ? ` ${unit}` : '';
  let min = v[0];
  let max = v[0];
  for (const x of v) {
    if (x < min) min = x;
    if (x > max) max = x;
  }
  return `${label}: latest ${fmt(v[v.length - 1])}${u}, min ${fmt(min)}${u}, max ${fmt(max)}${u} over ${v.length} point${v.length === 1 ? '' : 's'}`;
}

export function bytesToMB(bytes: number): number {
  return Number.isFinite(bytes) ? bytes / (1024 * 1024) : 0;
}

/**
 * Converts cumulative counters to a per-second rate. The first point is 0; a negative delta
 * (counter reset after a container restart) and a non-positive time step both yield 0.
 */
export function counterToRate(values: number[], timesMs: number[]): number[] {
  return values.map((v, i) => {
    if (i === 0) return 0;
    const dv = v - values[i - 1];
    const dt = (timesMs[i] - timesMs[i - 1]) / 1000;
    if (!Number.isFinite(dv) || !Number.isFinite(dt) || dv < 0 || dt <= 0) return 0;
    return dv / dt;
  });
}

/**
 * Converts per-bucket byte deltas (already "bytes transferred in this bucket") to a per-second rate.
 * dt is the gap to the previous point; the first point uses the gap to the next one, or `nominalMs`
 * when there is only one point. A non-positive gap yields 0.
 */
export function deltaToRate(values: number[], timesMs: number[], nominalMs = 60_000): number[] {
  return values.map((v, i) => {
    let dtMs: number;
    if (i > 0) dtMs = timesMs[i] - timesMs[i - 1];
    else if (values.length > 1) dtMs = timesMs[1] - timesMs[0];
    else dtMs = nominalMs;
    const dt = dtMs / 1000;
    if (!Number.isFinite(v) || !Number.isFinite(dt) || dt <= 0 || v < 0) return 0;
    return v / dt;
  });
}

export interface RateUnit {
  unit: 'KB/s' | 'MB/s';
  divisor: number;
}

/** Picks KB/s or MB/s for a series of bytes-per-second values based on the largest one. */
export function pickRateUnit(bytesPerSec: number[]): RateUnit {
  const max = bytesPerSec.reduce((m, x) => (Number.isFinite(x) && x > m ? x : m), 0);
  return max >= 1024 * 1024 ? { unit: 'MB/s', divisor: 1024 * 1024 } : { unit: 'KB/s', divisor: 1024 };
}
